import { Box, CircularProgress, Dialog, DialogActions, DialogContent, Tab, Tabs } from "@mui/material"
import type { ChangeEvent, FC, SyntheticEvent } from "react"
import { useRef, useState } from "react"

import { useGetApprovedProducts } from "../../hooks/useGetApprovedProducts"
import { useProductSelection } from "../../hooks/useProductSelection"

import { ApprovedProductsTable } from "./ApprovedProductsTable"
import { ProductSelectionChips } from "./ProductSelectionChips"
import { ProductSelectionWipPanel } from "./ProductSelectionWipPanel"
import { QuotedProductsTabPanel } from "./QuotedProductsTabPanel"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import SearchInput from "@/components/SearchInput/SearchInput"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { NotificationType } from "@/schemas/notification"
import { PRODUCT_SELECTION_TABS } from "@/screens/CustomerOrders/constants"
import { ProductSelectionTab, type SelectedProduct } from "@/screens/CustomerOrders/types"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import colors from "@/styles/colors"
import useDebounce from "@/utils/useDebounce"

const activeTabStyles = {
  color: colors.primary.main,
  fontWeight: 500,
  fontSize: 14,
  boxShadow: `inset 0 -2px 0 ${colors.primary.main}`,
  px: 3,
  py: 1,
}

const inactiveTabStyles = {
  color: colors.grey[700],
  fontWeight: 500,
  fontSize: 14,
}

interface ProductSelectionDialogProps {
  companyId: string
  shipToLocationId: string
  cpoProductIds?: number[]
  onClose: () => void
  onProductsAdded: (products: SelectedProduct[]) => Promise<boolean> | boolean
}

export const ProductSelectionDialog: FC<ProductSelectionDialogProps> = ({
  companyId,
  shipToLocationId,
  cpoProductIds = [],
  onClose,
  onProductsAdded,
}) => {
  const [activeTab, setActiveTab] = useState<ProductSelectionTab>(ProductSelectionTab.Approved)
  const [query, setQuery] = useState("")
  const debouncedQuery = useDebounce(query, DEBOUNCE_DELAY_MS)

  const { approvedProducts, isFetching, error, loadMore, refetch } = useGetApprovedProducts({
    companyId,
    shipToLocationId,
    query: debouncedQuery,
  })
  const { selectedProducts, rowSelection, chips, handleSelectionChange, handleChipRemove, clearSelection } =
    useProductSelection({
      products: approvedProducts,
    })

  const containerRef = useRef<HTMLDivElement>(null)
  const [throttledLoadMore] = useThrottle(() => loadMore(), 200)
  const handleScroll = useScrollLoadMore({
    fetching: isFetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: containerRef,
  })

  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const canSubmit = selectedProducts.length > 0 && !isSubmitting
  const isInitialLoading = isFetching && !approvedProducts.length

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }

  const handleTabChange = (_event: SyntheticEvent, value: ProductSelectionTab) => {
    setActiveTab(value)
  }

  const handleAssociationComplete = () => {
    setActiveTab(ProductSelectionTab.Approved)
    refetch()
  }

  const handleAddProducts = async () => {
    setIsSubmitting(true)
    const isSuccess = await onProductsAdded(selectedProducts)
    setIsSubmitting(false)

    if (isSuccess) {
      clearSelection()
      return
    }

    notify({ type: NotificationType.Error, message: "Failed to add products. Please try again." })
  }

  const activeTabLabel = PRODUCT_SELECTION_TABS.find((tab) => tab.value === activeTab)?.label ?? ""
  const isWipTab = activeTab === ProductSelectionTab.Sampled || activeTab === ProductSelectionTab.LinkedCompanies

  return (
    <Dialog open fullWidth maxWidth='lg' onClose={onClose} data-testid='customer-orders.product-selection-dialog'>
      <DialogHeader title='Add Products' onClose={onClose} />
      <DialogContent className='flex flex-col gap-4' classes={{ root: "px-10" }}>
        <SearchInput
          placeholder='Search Product ID, Description or Customer Code...'
          value={query}
          onChange={handleSearchInputChange}
          className='w-full max-w-[524px]'
          data-testid='customer-orders.product-selection-dialog.search-input'
        />

        <ProductSelectionChips chips={chips} onRemove={handleChipRemove} />

        <Tabs value={activeTab} onChange={handleTabChange}>
          {PRODUCT_SELECTION_TABS.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              sx={activeTab === tab.value ? activeTabStyles : inactiveTabStyles}
              data-testid={`customer-orders.product-selection-dialog.tab-${tab.value.toLowerCase().replace(/_/g, "-")}`}
            />
          ))}
        </Tabs>

        {activeTab === ProductSelectionTab.Approved && isInitialLoading && (
          <Box className='flex items-center justify-center p-8'>
            <CircularProgress data-testid='customer-orders.product-selection-dialog.loading-indicator' />
          </Box>
        )}

        {activeTab === ProductSelectionTab.Approved && !isInitialLoading && (
          <Box className='flex min-h-0 flex-1 overflow-y-auto'>
            <ApprovedProductsTable
              data={approvedProducts}
              isFetching={isFetching}
              error={error}
              rowSelection={rowSelection}
              cpoProductIds={cpoProductIds}
              containerRef={containerRef}
              onScroll={handleScroll}
              onSelectionChange={handleSelectionChange}
            />
          </Box>
        )}

        {activeTab === ProductSelectionTab.Quoted && (
          <QuotedProductsTabPanel
            companyId={companyId}
            shipToLocationId={shipToLocationId}
            query={debouncedQuery}
            onAssociationComplete={handleAssociationComplete}
          />
        )}

        {isWipTab && <ProductSelectionWipPanel tabLabel={activeTabLabel} />}
      </DialogContent>
      <DialogActions className='flex justify-between px-10 pb-6'>
        <Button
          appearance='outlined'
          onClick={onClose}
          data-testid='customer-orders.product-selection-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!canSubmit}
          loading={isSubmitting}
          onClick={handleAddProducts}
          data-testid='customer-orders.product-selection-dialog.add-button'
        >
          Add Products
        </Button>
      </DialogActions>
    </Dialog>
  )
}
